import React from 'react';
import moment from 'moment';
import classNames from 'classnames';

/**
 * The line under a leg's heading: where it goes, when, and who is on it.
 *
 * <p>Shared by both legs and by both kinds of card, because it describes the booking rather than the
 * vessel — a sailing with fare classes and one without are the same journey on the same date.</p>
 *
 * <p>It no longer counts the party. On a phone the step already opens with `For 1 vehicle, 1 adult`
 * directly above this line, and on a desktop the sidebar carries the same counts — so the number was
 * being given twice on every width, in the one line a booker reads on the way to the prices.</p>
 *
 * <p>The arrow does the work "to" was doing, and carries the direction in a glyph rather than a
 * word; the dots separate three facts that are not a sentence. Both come straight from the design,
 * and both matter more than they look: this line is read at a glance on the way to the prices.</p>
 */
interface Props {
	from: string | undefined;
	to: string | undefined;
	/** Omitted on the freight tab, where the date is the heading above this line. */
	date?: Date | string | null;
	/**
	 * Drop the date at phone width, where the day rail below is already showing it.
	 *
	 * <p>Off once a sailing is chosen: the rail goes with it, and the date becomes the only place
	 * the booker can see which day they are looking at.</p>
	 */
	dateInRail?: boolean;
}

export default function TripLegSummary({ from, to, date, dateInRail }: Props) {
	return (
		<h6 className="ticket-tab-text ticket-tab__leg">
			<span className="ticket-tab__leg-route">
				{from}
				<i className="ticket-tab__leg-arrow" aria-hidden="true">
					→
				</i>
				{to}
			</span>

			{date && (
				<span
					className={classNames('ticket-tab__leg-date', {
						'ticket-tab__leg-date--in-rail': dateInRail,
					})}
				>
					<i className="ticket-tab__leg-sep" aria-hidden="true" />
					{moment(date).format('D MMM YYYY')}
				</span>
			)}

		</h6>
	);
}
